import { Controller } from "@hotwired/stimulus";
import { minutesFromLabel } from "./helpers/time_combobox";
import { escapeHtml } from "./helpers/html";

// "Attendance Days Preview" panel for the event create/edit form. Shows the
// first few generated occurrence days (date + start→end time, "Day N") computed
// LIVE from the current form inputs, with a count of the remaining days and a
// note that they're individually manageable later. Only shown for multi-day and
// recurring events. Mirrors the server occurrence generation:
//   - multiple_event : every day from start_date..end_date
//   - recurring weekly: each selected weekday from start_date..end_date
//   - recurring monthly: day_of_month(N) or day_of_week(:wd => [n]/-1) per month
export default class extends Controller {
  static targets = ["list", "footer"];
  static values = {
    previewCount: { type: Number, default: 3 },
    dayLabel: { type: String, default: "Day {number}" },
    moreLabel: { type: String, default: "+{count} more days" },
    note: { type: String, default: "These can be individually managed later." },
    nextDayLabel: { type: String, default: "next day" },
    // Field-name mapping so this controller works on BOTH the manual event form
    // (event[...] inputs inside tab panes) and the onboarding form (flat inputs
    // with their own names). Defaults match the manual event form. The onboarding
    // form overrides these (e.g. startDateField → "first_event_date").
    startDateField: { type: String, default: "start_date_local" },
    endDateField: { type: String, default: "end_date_local" },
    startTimeField: { type: String, default: "start_time_local" },
    endTimeField: { type: String, default: "end_time_local" },
    recurrenceTypeField: { type: String, default: "recurrence_type" },
    monthlyRuleField: { type: String, default: "monthly_recurring_rule" },
    weeklyDaysField: { type: String, default: "recurring_days" },
    // How the field names are wrapped: "event[NAME]" (manual form) vs "NAME"
    // (onboarding). Set to "flat" on onboarding.
    nameStyle: { type: String, default: "scoped" },
    // CSS selector for the element whose value is the event_type. Defaults to the
    // manual form's hidden target; onboarding points it at its format radios.
    eventTypeSelector: { type: String, default: '[data-event-date-time-target="eventType"]' },
    // When true, read inputs from the whole root rather than the active tab pane
    // (the onboarding form has no tab panes). Defaults to false (manual form).
    flatLayout: { type: Boolean, default: false },
    // Optional input name carrying an explicit occurrence count (onboarding's
    // "Number of events"). When set, a RECURRING event shows exactly that many
    // days. Empty (manual form) → window-based, no cap.
    occurrenceCountField: { type: String, default: "" },
  };

  connect() {
    // The element whose form inputs drive the preview. Manual form: the
    // .component-wrapper date/time section. Onboarding (flat): the whole form,
    // since the inputs live across the page rather than in one wrapper.
    this.wrapper =
      this.element.closest(".component-wrapper") ||
      (this.flatLayoutValue && this.element.closest("form")) ||
      this.element;
    // Recompute on any relevant form change (date/time/frequency/day toggles)
    // and on tab switches (clicking a tab sets event_type without firing change).
    this._refresh = () => this.render();
    this.wrapper.addEventListener("change", this._refresh);
    this.wrapper.addEventListener("click", this._refresh);
    this.render();
  }

  disconnect() {
    this.wrapper.removeEventListener("change", this._refresh);
    this.wrapper.removeEventListener("click", this._refresh);
  }

  // ---- read current form state -------------------------------------------

  // On the manual form all three tab panes (single/multiple/recurring) render
  // fields with the SAME names, so read only from the ACTIVE pane. The onboarding
  // form has no tab panes (flatLayout), so read from the whole root.
  get activePane() {
    if (this.flatLayoutValue) return this.wrapper;
    return this.wrapper.querySelector(".tab-pane.show.active") || this.wrapper;
  }

  // Build the input `name` for a field, honoring the configured wrapping:
  // "event[NAME]" on the manual form, bare "NAME" on onboarding.
  fieldName(field) {
    return this.nameStyleValue === "flat" ? field : `event[${field}]`;
  }

  val(field) {
    const el = this.activePane.querySelector(`[name="${this.fieldName(field)}"]`);
    return el ? el.value.trim() : "";
  }

  // recurrence_type and monthly_recurring_rule each have BOTH a hidden field and
  // a user-facing <select> sharing the name; the hidden one is blank on a new
  // event, so read the <select> (the actual choice).
  selectVal(field) {
    const el = this.activePane.querySelector(`select[name="${this.fieldName(field)}"]`);
    return el ? el.value.trim() : "";
  }

  get eventType() {
    const el = this.wrapper.querySelector(this.eventTypeSelectorValue);
    if (!el) return "";
    // A hidden input/field exposes .value; a set of radios exposes the checked one.
    if (el.value !== undefined && el.type !== "radio") return el.value;
    const checked = this.wrapper.querySelector(`${this.eventTypeSelectorValue}:checked`);
    return checked ? checked.value : "";
  }

  get weeklyDays() {
    const name = this.nameStyleValue === "flat"
      ? `${this.weeklyDaysFieldValue}[]`
      : `event[${this.weeklyDaysFieldValue}][]`;
    return Array.from(
      this.activePane.querySelectorAll(`input[name="${name}"]:checked`)
    ).map((cb) => cb.value);
  }

  // ---- render -------------------------------------------------------------

  render() {
    const type = this.eventType;
    if (type !== "multiple_event" && type !== "recurring_event") return this.hide();

    const occurrences = this.computeOccurrences(type);
    if (occurrences.length === 0) return this.hide();

    const startTime = this.val(this.startTimeFieldValue);
    const endTime = this.val(this.endTimeFieldValue);
    const timeText = this.timeRangeText(startTime, endTime);

    const shown = occurrences.slice(0, this.previewCountValue);
    this.listTarget.innerHTML = shown.map((d, i) => this.row(d, i + 1, timeText)).join("");

    const remaining = occurrences.length - shown.length;
    this.footerTarget.innerHTML = this.footerHtml(remaining);

    this.element.classList.remove("d-none");
  }

  hide() {
    this.element.classList.add("d-none");
  }

  // "1:00 PM → 2:00 AM (next day)" when the end clock is at/before the start
  // clock (the block runs past midnight); plain "start → end" otherwise.
  // Builds the time-range text. startTime/endTime are user-typed time values and
  // the label is an i18n string, so both are escaped before they reach innerHTML.
  timeRangeText(startTime, endTime) {
    if (!startTime || !endTime) return "";
    const startMin = minutesFromLabel(startTime);
    const endMin = minutesFromLabel(endTime);
    const overnight = startMin != null && endMin != null && endMin <= startMin;
    const suffix = overnight ? ` (${escapeHtml(this.nextDayLabelValue)})` : "";
    return `${escapeHtml(startTime)} → ${escapeHtml(endTime)}${suffix}`;
  }

  row(date, dayNumber, timeText) {
    // heading comes from toLocaleDateString and dayNumber is an integer (both
    // safe); timeText is already escaped in timeRangeText; the label is i18n →
    // escape it. dayNumber is substituted AFTER escaping so the digits survive.
    const heading = date.toLocaleDateString("en-US", {
      weekday: "short", month: "short", day: "numeric",
    });
    const badge = escapeHtml(this.dayLabelValue).replace("{number}", dayNumber);
    return `
      <div class="attendance-day-row">
        <div class="attendance-day-info">
          <div class="attendance-day-date">${escapeHtml(heading)}</div>
          ${timeText ? `<div class="attendance-day-time">${timeText}</div>` : ""}
        </div>
        <span class="attendance-day-badge">${badge}</span>
      </div>`;
  }

  footerHtml(remaining) {
    const more = remaining > 0
      ? `<div class="attendance-days-more">${escapeHtml(this.moreLabelValue).replace("{count}", remaining)}</div>`
      : "";
    return `${more}<div class="attendance-days-note">${escapeHtml(this.noteValue)}</div>`;
  }

  // ---- occurrence computation (mirrors the server) ------------------------

  computeOccurrences(type) {
    const startDate = this.parseDate(this.val(this.startDateFieldValue));
    const endDate = this.parseDate(this.endDateValue());
    if (!startDate || !endDate || endDate < startDate) return [];

    if (type === "multiple_event") return this.eachDay(startDate, endDate);

    const recurrence = this.recurrenceTypeValue();
    let occ = [];
    if (recurrence === "weekly") occ = this.weeklyOccurrences(startDate, endDate, this.weeklyDays);
    else if (recurrence === "monthly") occ = this.monthlyOccurrences(startDate, endDate, this.monthlyRuleValue());

    // Onboarding: "Number of events" caps the series to exactly N occurrences,
    // so the preview must show exactly N (the window endpoint alone could still
    // include a trailing same-week extra match when 2+ weekday chips are picked).
    const count = this.occurrenceCount();
    return count ? occ.slice(0, count) : occ;
  }

  // Explicit occurrence count from the configured field, or null (manual form).
  occurrenceCount() {
    if (!this.occurrenceCountFieldValue) return null;
    const el = this.activePane.querySelector(`[name="${this.fieldName(this.occurrenceCountFieldValue)}"]`);
    const n = el ? parseInt(el.value, 10) : NaN;
    return Number.isFinite(n) && n > 0 ? n : null;
  }

  // recurrence_type / monthly_recurring_rule may be a <select> (manual form) or a
  // checked radio / derived hidden value (onboarding). Try select first, then
  // fall back to any input with the field name.
  recurrenceTypeValue() {
    return this.selectVal(this.recurrenceTypeFieldValue) || this.val(this.recurrenceTypeFieldValue);
  }

  monthlyRuleValue() {
    return this.selectVal(this.monthlyRuleFieldValue) || this.val(this.monthlyRuleFieldValue);
  }

  // On onboarding, the end date for a recurring/multi-day series is derived (the
  // manual end_date field is absent); fall back to the start date so a single
  // explicit end isn't required for the preview to compute multi-day spans.
  endDateValue() {
    return this.val(this.endDateFieldValue);
  }

  eachDay(start, end) {
    const out = [];
    // The server caps a multi-day span at 31 days; bound the client walk too so a
    // devtools-tampered end_date can't build an unbounded date list (self-DoS).
    const MAX = 366;
    for (let d = new Date(start); d <= end && out.length < MAX; d.setDate(d.getDate() + 1)) {
      out.push(new Date(d));
    }
    return out;
  }

  // IceCube weekly.day(d) from start_date: every matching weekday in range
  // (including the start date itself if it matches).
  weeklyOccurrences(start, end, days) {
    if (days.length === 0) return [];
    const wanted = new Set(days.map((d) => WEEKDAY_INDEX[d.toLowerCase()]));
    const out = [];
    for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
      if (wanted.has(d.getDay())) out.push(new Date(d));
    }
    return out;
  }

  // monthly_recurring_rule is one of:
  //   day_of_month(15)
  //   day_of_week(:wednesday => [2])   (nth weekday)
  //   day_of_week(:wednesday => [-1])  (last weekday)
  monthlyOccurrences(start, end, rule) {
    if (!rule) return [];
    const out = [];

    const dom = rule.match(/day_of_month\((\d{1,2})\)/);
    if (dom) {
      const day = parseInt(dom[1], 10);
      let cursor = new Date(start.getFullYear(), start.getMonth(), 1);
      while (cursor <= end) {
        const last = new Date(cursor.getFullYear(), cursor.getMonth() + 1, 0).getDate();
        const occ = new Date(cursor.getFullYear(), cursor.getMonth(), Math.min(day, last));
        if (occ >= start && occ <= end) out.push(occ);
        cursor.setMonth(cursor.getMonth() + 1);
      }
      return out;
    }

    const dow = rule.match(/day_of_week\(:(\w+)\s*=>\s*\[(-?\d{1,2})\]\)/);
    if (dow) {
      const weekday = WEEKDAY_INDEX[dow[1].toLowerCase()];
      const nth = parseInt(dow[2], 10);
      let cursor = new Date(start.getFullYear(), start.getMonth(), 1);
      while (cursor <= end) {
        const occ = nth === -1
          ? this.lastWeekdayOfMonth(cursor.getFullYear(), cursor.getMonth(), weekday)
          : this.nthWeekdayOfMonth(cursor.getFullYear(), cursor.getMonth(), weekday, nth);
        if (occ && occ >= start && occ <= end) out.push(occ);
        cursor.setMonth(cursor.getMonth() + 1);
      }
      return out;
    }

    return out;
  }

  nthWeekdayOfMonth(year, month, weekday, nth) {
    const first = new Date(year, month, 1);
    const offset = (weekday - first.getDay() + 7) % 7;
    const day = 1 + offset + (nth - 1) * 7;
    const last = new Date(year, month + 1, 0).getDate();
    return day <= last ? new Date(year, month, day) : null;
  }

  lastWeekdayOfMonth(year, month, weekday) {
    const last = new Date(year, month + 1, 0);
    const offset = (last.getDay() - weekday + 7) % 7;
    return new Date(year, month, last.getDate() - offset);
  }

  // "YYYY-MM-DD" → local Date (avoid the UTC shift of new Date(str)).
  parseDate(str) {
    if (!str) return null;
    const [y, m, d] = str.split("-").map((n) => parseInt(n, 10));
    if (!y || !m || !d) return null;
    return new Date(y, m - 1, d);
  }
}

const WEEKDAY_INDEX = {
  sunday: 0, monday: 1, tuesday: 2, wednesday: 3, thursday: 4, friday: 5, saturday: 6,
};
